import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck } from 'lucide-react';

import {
  loadNotificationPreferences,
  loadNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationPreference,
} from './api';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import {
  notificationEventMessage, notificationEventTitle,
  notificationTopicDescription, notificationTopicLabel,
} from './labels';

function when(value) {
  return value ? new Date(Number(value)).toLocaleString() : '—';
}

// The feed is a person's own, so it is delivered on the user-scoped
// 'notifications' topic rather than broadcast. The reconciliation poll is
// deliberately long: the socket is what makes a notification appear promptly,
// and the poll only covers a dropped connection.
export function NotificationBell({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const panelRef = useRef(null);

  const load = useCallback(() => loadNotifications({ limit: 30 }), []);
  const feed = useLiveResource(['notifications'], load);

  // Clicking outside closes the panel. Without this it stays open behind the
  // next screen the user opens.
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  const unread = feed.data ? feed.data.unread : 0;
  const notifications = feed.data ? feed.data.notifications : [];

  async function readOne(notification) {
    setError('');
    try {
      if (!notification.isRead) await markNotificationRead(notification.id);
      if (notification.actionRoute && onNavigate) {
        const [pathname, query = ''] = notification.actionRoute.split('?');
        const route = pathname.startsWith('/xomics/')
          ? pathname.slice('/xomics/'.length)
          : new URLSearchParams(query).get('view');
        if (route) onNavigate(query ? `${route}?${query}` : route);
        setOpen(false);
      }
      feed.refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  async function readAll() {
    setError('');
    setBusy(true);
    try {
      await markAllNotificationsRead();
      feed.refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="xo-bell" ref={panelRef}>
      <button
        type="button"
        className="xo-icon-btn xo-bell-btn"
        aria-expanded={open}
        aria-label={unread > 0 ? tx("ui.notifications.notifications_unread_unread_25d18c18", { unread: unread }) : tx("ui.notifications.notifications_753a22b2")}
        onClick={() => setOpen((current) => !current)}
      >
        <Bell aria-hidden="true" />
        {unread > 0 && <span className="xo-bell-count">{unread}</span>}
      </button>

      {open && (
        <div className="xo-bell-panel" role="dialog" aria-label={tx("ui.notifications.notifications_753a22b2")}>
          <div className="xo-bell-top">
            <strong>{tx("ui.notifications.notifications_753a22b2")}</strong>
            <button
              type="button"
              className="xo-link"
              disabled={busy || unread === 0}
              onClick={readAll}
            >
              <CheckCheck size={14} aria-hidden="true" /> {tx("ui.notifications.mark_all_read_8958e22c")}
            </button>
          </div>

          <div className="xo-bell-feed">
            {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
            {feed.loading && <p className="xo-bell-empty">{tx("ui.notifications.loading_33ce4174")}</p>}
            {feed.error && <p className="xo-alert t-bad" role="alert">{feed.error}</p>}
            {!feed.loading && notifications.length === 0 && (
              <p className="xo-bell-empty">{tx("ui.notifications.nothing_yet_dbf1d192")}</p>
            )}

            <ul className="xo-bell-list">
              {notifications.map((notification) => (
                <li key={notification.id} className={notification.isRead ? '' : 'is-unread'}>
                  <button type="button" className="xo-bell-item" onClick={() => readOne(notification)}>
                    <span className="xo-bell-item-head">
                      <span className="xo-bell-name">{notificationEventTitle(notification.type)}</span>
                      <span className="xo-bell-time">{when(notification.createdAt)}</span>
                    </span>
                    <span className="xo-bell-msg">{notificationEventMessage(notification.type, notification.messageVariables)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// Which topics this account may receive is decided by its permissions, so this
// list is shorter for a reviewer than for an administrator. The server returns
// only the entitled topics; nothing is filtered here.
export function NotificationPreferences() {
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => loadNotificationPreferences(), []);
  const prefs = useLiveResource(['session'], load);

  async function toggle(topic, field) {
    setError('');
    setSaving(topic.key);
    try {
      await updateNotificationPreference(topic.key, {
        inapp: field === 'inapp' ? !topic.inapp : topic.inapp,
        email: field === 'email' ? !topic.email : topic.email,
        sms: field === 'sms' ? !topic.sms : topic.sms,
      });
      prefs.refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving('');
    }
  }

  return (
    <section className="xo-account-settings-panel xo-notification-settings">
      <header>
        <span><Bell aria-hidden="true" /></span>
        <div>
          <p className="xo-eyebrow">{tx("ui.notifications.notifications_753a22b2")}</p>
          <h2>{tx("ui.notifications.what_you_are_told_about_1e832fcc")}</h2>
          <p>{tx("ui.notifications.these_are_the_topics_your_role_is_entitled_to_turning__76cf4a73")}</p>
        </div>
      </header>

      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      {prefs.loading && <p className="xo-muted">{tx("ui.notifications.loading_33ce4174")}</p>}
      {prefs.error && <p className="xo-alert t-bad" role="alert">{prefs.error}</p>}

      {prefs.data && (
        <>
          <div className="xo-preference-columns" aria-hidden="true">
            <span>{tx("ui.notifications.topic_7e13bd17")}</span>
            <span>{tx("ui.notifications.in_portal_1297dedd")}</span>
            <span>{tx("ui.notifications.email_84add5b2")}</span>
            <span>{tx("ui.notifications.sms_9e10d451")}</span>
          </div>
          <div className="xo-preference-list">
            {prefs.data.topics.map((topic) => (
              <article key={topic.key}>
                <div>
                  <strong>{notificationTopicLabel(topic.key)}</strong>
                  <p>{notificationTopicDescription(topic.key)}</p>
                </div>
                <label className="xo-preference-toggle">
                  <input type="checkbox" checked={topic.inapp} disabled={saving === topic.key}
                    aria-label={tx("ui.notifications.label_in_portal_1678ef63", { label: notificationTopicLabel(topic.key) })}
                    onChange={() => toggle(topic, 'inapp')} />
                  <span aria-hidden="true"><i /></span>
                </label>
                <label className="xo-preference-toggle">
                  <input type="checkbox" checked={topic.email}
                    disabled={!prefs.data.emailAvailable || !prefs.data.hasEmail || saving === topic.key}
                    aria-label={tx("ui.notifications.label_by_email_b102087c", { label: notificationTopicLabel(topic.key) })}
                    onChange={() => toggle(topic, 'email')} />
                  <span aria-hidden="true"><i /></span>
                </label>
                <label className="xo-preference-toggle">
                  <input type="checkbox" checked={topic.sms}
                    disabled={!prefs.data.smsAvailable || !prefs.data.hasPhoneNumber || saving === topic.key}
                    aria-label={tx("ui.notifications.label_by_sms_75724312", { label: notificationTopicLabel(topic.key) })}
                    onChange={() => toggle(topic, 'sms')} />
                  <span aria-hidden="true"><i /></span>
                </label>
              </article>
            ))}
          </div>
          <div className="xo-settings-notes">
          {!prefs.data.emailAvailable && (
            <p className="xo-muted">
              {tx("ui.notifications.email_delivery_is_not_configured_on_this_server_so_it__eeb67a01")}
            </p>
          )}
          {prefs.data.emailAvailable && !prefs.data.hasEmail && (
            <p className="xo-muted">
              {tx("ui.notifications.your_account_has_no_email_address_so_email_cannot_be_s_90782d08")}
            </p>
          )}
          {!prefs.data.smsAvailable && (
            <p className="xo-muted">
              {tx("ui.notifications.sms_delivery_is_not_configured_on_this_server_so_it_ca_c1eb725f")}
            </p>
          )}
          {prefs.data.smsAvailable && !prefs.data.hasPhoneNumber && (
            <p className="xo-muted">
              {tx("ui.notifications.your_account_has_no_phone_number_so_sms_cannot_be_swit_8634bec7")}
            </p>
          )}
          </div>
        </>
      )}
    </section>
  );
}
